import React from "react";
import ServiceLayout from "../components/ServiceLayout";

const BentoBoxes = () => {
  const whatsIncluded = [
    {
      title: "Individual Packaging",
      description: "Each meal sealed in premium, eco-friendly compartments to maintain temperature and freshness."
    },
    {
      title: "Portion Control",
      description: "Calibrated portions with balanced macros — protein, carbs, and vegetables in optimal ratios."
    },
    {
      title: "Dietary Labels",
      description: "Clear labeling for Jain, vegan, keto, halal, gluten-free, and allergen information."
    },
    {
      title: "Temperature Retention",
      description: "Insulated packaging keeps food at serving temperature for extended hours."
    },
    {
      title: "Custom Branding",
      description: "Boxes branded with your company logo and employee names for personalization."
    },
    {
      title: "Contactless Delivery",
      description: "Pre-scheduled delivery to individual desks or designated pickup points."
    }
  ];

  const perfectFor = [
    "Board meetings and executive lunches",
    "Training sessions and workshops",
    "Travel days and off-site events",
    "Hybrid team gatherings",
    "Client presentations and pitches",
    "Focus group sessions"
  ];

  const pricing = [
    {
      name: "Standard",
      price: "₹180",
      unit: "/ box",
      description: "Balanced meal for everyday meetings",
      features: [
        "1 main + 1 side + salad",
        "Bread/roti + dessert",
        "Cutlery & napkin",
        "Basic labeling"
      ]
    },
    {
      name: "Premium",
      price: "₹250",
      unit: "/ box",
      description: "Enhanced variety for important occasions",
      features: [
        "2 mains + 2 sides + salad",
        "Premium bread basket",
        "Gourmet dessert",
        "Custom branding",
        "Dietary-specific options"
      ]
    },
    {
      name: "Executive",
      price: "₹350",
      unit: "/ box",
      description: "Boardroom-grade for C-suite and clients",
      features: [
        "3 gourmet selections",
        "Artisanal bread & dips",
        "Premium dessert & beverage",
        "Personalized name cards",
        "Thermal packaging"
      ]
    }
  ];

  const faqs = [
    { question: 'What are corporate meal boxes suitable for?', answer: 'They are suitable for office lunches, meetings, workshops, training sessions, travel days and corporate events where individual portions are preferred.' },
    { question: 'Can meal boxes be planned for dietary requirements?', answer: 'Yes. Menu planning can account for vegetarian, Jain and other dietary preferences discussed while planning the order.' },
    { question: 'Do you supply corporate meal boxes in Bangalore?', answer: 'Yes. The Office Meal Co. serves corporate and workplace requirements across Bengaluru, subject to location, order size and schedule.' }
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": 'Corporate Meal Boxes & Office Lunch Boxes in Bangalore',
      "serviceType": 'Corporate meal boxes and office lunch boxes',
      "url": 'https://theofficemealco.com/bento-boxes',
      "provider": { "@type": "Organization", "name": "The Office Meal Co.", "url": "https://theofficemealco.com/" },
      "areaServed": { "@type": "City", "name": "Bengaluru" }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map((faq) => ({
        "@type": "Question", "name": faq.question,
        "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
      }))
    }
  ];

  return (
    <ServiceLayout
      title="Corporate Meal Boxes & Bento Boxes in Bangalore"
      subtitle="Individually packed corporate meal boxes for office lunches, meetings, training sessions, events and teams across Bangalore."
      heroImage="/images/bento-boxes.png"
      whatsIncluded={whatsIncluded}
      perfectFor={perfectFor}
      seoTitle="Corporate Meal Boxes & Office Lunch Boxes Bangalore"
      seoDescription="Corporate meal boxes and bento boxes in Bangalore for meetings, training sessions, office lunches and events, with individual packaging and dietary labels."
      seoKeywords="corporate meal box, corporate lunch box delivery, lunch box for office, bento boxes bangalore, meal boxes for corporate events"
      canonicalUrl="https://theofficemealco.com/bento-boxes"
      structuredData={structuredData}
    >
      {/* Custom content */}
      <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
        <div className="relative">
          <div className="absolute -inset-4 bg-[#E8DDCB] rounded-full opacity-20 blur-3xl"></div>
          <img 
            src="/images/bento-compartments.png" 
            alt="Bento box compartments" 
            className="relative w-full h-[400px] object-cover shadow-lg"
          />
        </div>
        <div>
          <span className="text-[#CBA365] text-xs tracking-[0.24em] uppercase font-medium mb-4 block font-sans">
             • The Box
          </span>
          <h2 className="font-serif text-3xl text-[#1E3A5F] mb-6">
            Packed office meals for meetings, teams and events
          </h2>
          <p className="text-[#64748B] leading-relaxed mb-6 font-sans">
            Our bento boxes aren't just containers — they're carefully designed meal experiences. 
            Each compartment is sized to maintain optimal food temperature and prevent mixing of flavors. 
            The eco-friendly, microwave-safe packaging means your team can enjoy their meal whenever 
            their schedule allows.
          </p>
          <ul className="space-y-3">
            {[
              "Compostable sugarcane fiber base",
              "Leak-proof PLA lid (plant-based)",
              "Microwave and freezer safe",
              "Custom branding available"
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-[#64748B] font-sans">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M15 4.5L6.75 12.75L3 9" stroke="#CBA365" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Box Preview */}
      <div className="bg-[#F3EFE6] p-8 md:p-12 mb-16">
        <h3 className="font-serif text-2xl text-[#1E3A5F] mb-8 text-center">What can go into a corporate meal box</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
          <div className="bg-white p-4 text-center shadow-sm">
            <div className="w-16 h-16 bg-[#F3EFE6] mx-auto mb-2 flex items-center justify-center">
              <span className="text-2xl">🍛</span>
            </div>
            <span className="text-sm text-[#1E3A5F] font-medium font-sans">Main Course</span>
          </div>
          <div className="bg-white p-4 text-center shadow-sm">
            <div className="w-16 h-16 bg-[#F3EFE6] mx-auto mb-2 flex items-center justify-center">
              <span className="text-2xl">🥗</span>
            </div>
            <span className="text-sm text-[#1E3A5F] font-medium font-sans">Fresh Salad</span>
          </div>
          <div className="bg-white p-4 text-center shadow-sm">
            <div className="w-16 h-16 bg-[#F3EFE6] mx-auto mb-2 flex items-center justify-center">
              <span className="text-2xl">🍞</span>
            </div>
            <span className="text-sm text-[#1E3A5F] font-medium font-sans">Artisan Bread</span>
          </div>
          <div className="bg-white p-4 text-center shadow-sm">
            <div className="w-16 h-16 bg-[#F3EFE6] mx-auto mb-2 flex items-center justify-center">
              <span className="text-2xl">🍮</span>
            </div>
            <span className="text-sm text-[#1E3A5F] font-medium font-sans">Dessert</span>
          </div>
        </div>
      </div>

      <div className="mt-16 bg-[#F3EFE6] p-8 md:p-12">
        <h2 className="font-serif text-3xl text-[#1E3A5F] mb-8">Frequently asked questions</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {faqs.map((faq) => (
            <div key={faq.question} className="bg-white p-6 border border-[#E8DDCB]">
              <h3 className="font-medium text-[#1E3A5F] mb-3 font-sans">{faq.question}</h3>
              <p className="text-sm text-[#64748B] leading-relaxed font-sans">{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </ServiceLayout>
  );
};

export default BentoBoxes;
